import React, { useMemo, useState, useEffect } from "react";
import styles from "./TransactionsSummaryCard.module.css";

import {
  sumDeltas,
  bucketByDay,
  dailySeriesFromBuckets,
  daysInRange,
  movingAverage,
  inflowOutflowByPeriod,
  choosePeriod,
} from "../../../../app/app-utils/txStats";
import { formatAmountDisplay } from "../TransactionsTableComponent/TransactionsTableComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowTrendUp, faArrowTrendDown, faArrowRightArrowLeft, faTags } from "@fortawesome/free-solid-svg-icons";

import { ChartBox, cssVar } from "./charts/ChartPrimitives/ChartPrimitives";
import DonutTagPair from "./charts/DonutTagPair/DonutTagPair";
import BalanceOverTimeChart from "./charts/BalanceOverTimeChart/BalanceOverTimeChart";
import PeriodDropdown from "./charts/PeriodDropdown/PeriodDropdown";

const LS_KEY = "pp:txSummaryCollapsed";

function formatMaybeCurrency(n, currencyCode) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return String(n ?? "");
  try {
    if (!currencyCode) {
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return formatAmountDisplay(num, { currencyCode });
  } catch { return num.toFixed(2); }
}

export default function TransactionsSummaryCard({
  items = [],
  allItems = [],
  resolvedRange,
  account,
  defaultCollapsed = false,
  accountRows,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw === "1") setCollapsed(true);
      if (raw === "0") setCollapsed(false);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(LS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const { counts, totals, perDay } = useMemo(() => {
    const count = items.length;
    const { inflow, outflow, net } = sumDeltas(items, account?.id);

    let dayPairs = bucketByDay(items);
    if (!resolvedRange?.from || !resolvedRange?.to) {
      if (dayPairs.length > 90) dayPairs = dayPairs.slice(-90);
    }
    const series = dailySeriesFromBuckets(dayPairs, account?.id);

    return { counts: { count }, totals: { inflow, outflow, net }, perDay: series };
  }, [items, account?.id, resolvedRange?.from, resolvedRange?.to]);

  const rangeDays = useMemo(() => {
    if (resolvedRange?.from && resolvedRange?.to) return daysInRange(resolvedRange);
    return perDay.labels.length || 0;
  }, [resolvedRange?.from, resolvedRange?.to, perDay.labels.length]);

  const currencyCode = account?.currencyCode || account?.currency || null;

  // KPIs
  const perDayMA = useMemo(() => movingAverage(perDay.values, 7), [perDay.values]);
  const dayCount = rangeDays || 0;
  const avgDailyNet = dayCount ? (totals.net / dayCount) : 0;

  const trend = useMemo(() => {
    const vals = perDay.values || [];
    if (vals.length < 14) return null;
    const last7 = vals.slice(-7).reduce((a, b) => a + b, 0) / 7;
    const prev7 = vals.slice(-14, -7).reduce((a, b) => a + b, 0) / 7;
    const delta = last7 - prev7;
    const pct = prev7 !== 0 ? (delta / Math.abs(prev7)) : null;
    return { last7, prev7, delta, pct };
  }, [perDay.values]);

  // ======= Aggregated IO (with dropdown) =======
  const defaultIO = choosePeriod(rangeDays);
  const [ioPeriod, setIoPeriod] = useState(defaultIO);
  useEffect(() => setIoPeriod(choosePeriod(rangeDays)), [rangeDays]);

  const ioAgg = useMemo(
    () => inflowOutflowByPeriod(items, { period: ioPeriod, accountId: account?.id }),
    [items, ioPeriod, account?.id]
  );

  const ioAggData = useMemo(() => ({
    labels: ioAgg.labels,
    datasets: [
      {
        label: "Inflow",
        data: ioAgg.inflows,
        borderWidth: 0,
        backgroundColor: cssVar("--pp-chart-positive", "#20b15a"),
        stack: "io",
        order: 2,
      },
      {
        label: "Outflow",
        data: ioAgg.outflows.map(v => -v),
        borderWidth: 0,
        backgroundColor: cssVar("--pp-chart-negative", "#ef4444"),
        stack: "io",
        order: 2,
      },
    ],
  }), [ioAgg.labels, ioAgg.inflows, ioAgg.outflows]);

  const ioAggOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: true, labels: { color: cssVar("--pp-chart-tick", "#6b7280") } },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatMaybeCurrency(ctx.parsed.y, currencyCode)}` }
      },
      zoom: { zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: "x" }, pan: { enabled: true, mode: "x" } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: cssVar("--pp-chart-tick", "#6b7280"), maxTicksLimit: 12 } },
      y: {
        grid: { color: cssVar("--pp-chart-grid", "rgba(0,0,0,.06)") },
        ticks: { color: cssVar("--pp-chart-tick", "#6b7280"), callback: (val) => formatMaybeCurrency(val, currencyCode) },
      },
    },
  }), [currencyCode]);

  const ioRightNode = (
    <PeriodDropdown value={ioPeriod} onChange={setIoPeriod} />
  );

  return (
    <section className={`card ${styles.card}`} aria-label="Filtered summary">
      <div className={`card-body ${styles.body}`}>
        {/* Header */}
        <div className={styles.headerRow}>
          <div className={styles.titleWrap}>
            <h2 className={styles.title}>
              Summary{account?.name ? <span className={styles.subtle}> — {account.name}</span> : null}
            </h2>
            {(resolvedRange?.from || resolvedRange?.to) && (
              <div className={styles.range}>
                {resolvedRange.from || "…"} – {resolvedRange.to || "…"}
              </div>
            )}
          </div>

          <button
            type="button"
            className={`btn btn-sm btn-outline-secondary ${styles.collapseBtn}`}
            aria-expanded={!collapsed}
            aria-controls="pp-tx-summary-content"
            onClick={onToggle}
          >
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>

        {!collapsed && (
          <div id="pp-tx-summary-content" className={styles.content}>
            {/* KPIs */}
            <div className={`row g-3 ${styles.kpiRow}`}>
              <div className="col-6 col-md-3">
                <div className={`${styles.kpi} ${styles.kpiPositive}`}>
                  <div className={styles.kpiLabel}><FontAwesomeIcon icon={faArrowTrendUp} className={styles.kpiIcon} />Inflow</div>
                  <div className={`${styles.kpiValue} is-mono`}>{formatMaybeCurrency(totals.inflow, currencyCode)}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className={`${styles.kpi} ${styles.kpiNegative}`}>
                  <div className={styles.kpiLabel}><FontAwesomeIcon icon={faArrowTrendDown} className={styles.kpiIcon} />Outflow</div>
                  <div className={`${styles.kpiValue} is-mono`}>{formatMaybeCurrency(-totals.outflow, currencyCode)}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className={styles.kpi}>
                  <div className={styles.kpiLabel}><FontAwesomeIcon icon={faArrowRightArrowLeft} className={styles.kpiIcon} />Net</div>
                  <div className={`${styles.kpiValue} is-mono`}>{formatMaybeCurrency(totals.net, currencyCode)}</div>
                  {(() => {
                    const v = perDay.values || [];
                    if (v.length < 14) return null;
                    const last7 = v.slice(-7).reduce((a, b) => a + b, 0) / 7;
                    const prev7 = v.slice(-14, -7).reduce((a, b) => a + b, 0) / 7;
                    const delta = last7 - prev7;
                    const pct = prev7 !== 0 ? (delta / Math.abs(prev7)) : null;
                    return (
                      <div className={`${styles.chip} ${delta >= 0 ? styles.chipUp : styles.chipDown}`} title="Avg daily net: last 7 vs previous 7">
                        {delta >= 0 ? "▲" : "▼"} {formatMaybeCurrency(delta, currencyCode)}
                        {typeof pct === "number" && <span className={styles.chipPct}> ({(pct * 100).toFixed(0)}%)</span>}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className={styles.kpi}>
                  <div className={styles.kpiLabel}><FontAwesomeIcon icon={faTags} className={styles.kpiIcon} />Transactions</div>
                  <div className={`${styles.kpiValue} is-mono`}>{counts.count}</div>
                  {dayCount > 0 && <div className={styles.kpiSubtle}>{formatMaybeCurrency(avgDailyNet, currencyCode)} / day net</div>}
                </div>
              </div>
            </div>

            {/* Top: side-by-side donuts */}
            <div className={`row mt-1 g-3 ${styles.chartsRow}`}>
              <DonutTagPair
                items={items}
                currencyCode={currencyCode}
                excludeTopLevels={["Transfer", "Internal"]}
                leftTitle="Spend by tag"
                rightTitle="Income by tag"
                height={220}
              />
            </div>

            {/* Bottom: either split (account selected) or full (no account) */}
            <div className={`row mt-1 g-3 ${styles.chartsRow}`}>
              {account?.id ? (
                <>
                  <div className="col-12 col-xl-6">
                    <BalanceOverTimeChart
                      allItems={allItems}
                      items={items}
                      accountRows={accountRows}
                      account={account}
                      resolvedRange={resolvedRange}
                      defaultPeriod={choosePeriod(rangeDays)}
                    />
                  </div>
                  <div className="col-12 col-xl-6">
                    <ChartBox
                      title="Inflow vs Outflow (aggregated)"
                      type="bar"
                      data={ioAggData}
                      options={ioAggOptions}
                      height={260}
                      rightNode={ioRightNode}
                    />
                  </div>
                </>
              ) : (
                <div className="col-12">
                  <ChartBox
                    title="Inflow vs Outflow (aggregated)"
                    type="bar"
                    data={ioAggData}
                    options={ioAggOptions}
                    height={260}
                    rightNode={ioRightNode}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
